import { Link } from "react-router-dom";
import { faceDataPolicyHref } from "@/content/faceDataPolicy";
import { Trans, useTranslation } from "react-i18next";
import SiteFrame from "./SiteFrame";
import { BoxBrackets, BracketMark } from "./PhotoDemo";
import { AppStoreButton } from "./PathCards";
import { PhoneFrame, useAppShot } from "./appScreens";
import { EAGLE_DARK, PERSONAL_HERO } from "./images";
import { APP_PATH, FIND_PATH, STUDIO_PATH } from "./paths";
import { ProductLockup, SearchIcon } from "./products";
import { AMBER_DISPLAY, BAND, BTN_PRIMARY_MD, EYEBROW, FOCUS, H1, H2, HERO_FIGURE, HERO_TEXT, SECTION_Y, TEXT_LINK, WRAP } from "./ui";

/** What the app does, from the app page's own copy (home.personal.features.*). */
const FEATURES = ["grouped", "send", "receive", "private"] as const;

// Studio's two doors, in Studio's dark look so they read as Studio's, never as part of the app.
const STUDIO_CARD = "flex min-w-0 flex-col gap-4 rounded-2xl border border-[#121721] bg-[#121721] p-[clamp(22px,3vw,32px)] text-white shadow-sm";
const STUDIO_LINK = `inline-flex min-h-11 items-center gap-1.5 self-start rounded-md font-semibold text-[#EA8A23] underline-offset-4 hover:underline ${FOCUS}`;

/**
 * The home page (staging only until launch: src/lib/homeFlag.ts). photohawk.com leads with Photohawk, the app for
 * the photos of your people (owner, 21 Oct 2026: two brands, Photohawk and Photohawk Studio): what it does and how to
 * get it, then what it does in four lines and its privacy. Then the two Studio doors, in Studio's dark look: Find your
 * event photos (participants search a photographer's gallery, a Studio feature) and Photohawk Studio for
 * photographers. Nothing presents event galleries as part of the app.
 */
const HomePage = () => {
  const { t } = useTranslation();
  const s = (key: string) => t(`home.story.${key}`);
  const peopleShot = useAppShot("people");

  return (
    <SiteFrame page="home" path="home">
      {/* HERO: the app */}
      <section aria-labelledby="home-title">
        <div className={`${WRAP} flex flex-wrap items-center gap-x-14 gap-y-10 pb-[clamp(48px,6vw,80px)] pt-[clamp(24px,3.5vw,48px)]`}>
          <div className={HERO_TEXT}>
            <span className={`${EYEBROW} inline-flex items-center gap-2.5 self-start text-muted-foreground`}>
              <BracketMark />
              {s("hero.eyebrow")}
            </span>
            <h1 id="home-title" className={H1}>
              <Trans i18nKey="home.story.hero.title" components={{ hl: <span style={{ color: AMBER_DISPLAY }} /> }} />
            </h1>
            <p className="m-0 max-w-[32em] text-[clamp(17px,1.5vw,20px)] leading-relaxed text-muted-foreground">{s("hero.subtitle")}</p>
            <div className="flex flex-wrap items-center gap-3" data-testid="home-get-app">
              <Link to={APP_PATH} className={`${BTN_PRIMARY_MD} text-[15px]`} data-testid="home-get-app-link">
                {t("home.nav.getApp")}
              </Link>
              <AppStoreButton />
            </div>
            <p className="m-0 text-sm text-muted-foreground">
              <strong className="font-semibold text-foreground">{t("home.doors.personal.platforms")}</strong> · {t("home.doors.personal.signIn")}
            </p>
          </div>
          <figure className={`${HERO_FIGURE} pb-[34px]`}>
            <div className="relative aspect-[3/2] w-full overflow-hidden rounded-xl shadow-lg">
              <img
                src={PERSONAL_HERO.src}
                srcSet={PERSONAL_HERO.srcSet}
                sizes="(min-width: 1024px) 640px, 100vw"
                width={PERSONAL_HERO.width}
                height={PERSONAL_HERO.height}
                alt={t("home.personal.heroAlt")}
                className="block h-full w-full object-cover"
              />
              {PERSONAL_HERO.faces.map((f) => (
                <span key={f.name}>
                  <BoxBrackets box={f.box} arm="28%" delay={f.delay} />
                  <span
                    aria-hidden="true"
                    className="absolute rounded bg-[#EA8A23] px-[9px] py-[3px] text-[13px] font-bold text-[#121721]"
                    style={{ left: `${f.box[0]}%`, top: `${f.box[1] + f.box[3] + 1.6}%` }}
                  >
                    {f.name}
                  </span>
                </span>
              ))}
            </div>
            {/* The app, over the photo's left edge: the People screen. */}
            <div className="absolute bottom-0 left-3 w-[23%] max-w-[190px] -rotate-[4deg] sm:w-[27%] lg:left-[-3%]" data-testid="home-hero-phone">
              <PhoneFrame shot={peopleShot} alt={t("home.personal.screens.alt.people")} sizes="(min-width: 1024px) 180px, 27vw" eager />
            </div>
            <figcaption className="absolute bottom-0 right-3 flex items-center gap-2.5 rounded-xl border border-border bg-card py-2.5 pl-2.5 pr-3.5 text-foreground shadow-lg">
              <img src={EAGLE_DARK} alt="" width={26} height={27} className="h-[27px] w-[26px] object-contain" />
              <span className="text-sm font-semibold">{t("home.personal.heroCaption")}</span>
            </figcaption>
          </figure>
        </div>
      </section>

      {/* WHAT THE APP DOES */}
      <section aria-labelledby="home-app" className={`${BAND} border-y border-border`}>
        <div className={`${WRAP} ${SECTION_Y} flex flex-col gap-9`}>
          <div className="flex flex-wrap items-end justify-between gap-x-12 gap-y-4">
            <h2 id="home-app" className={`${H2} max-w-[16em]`}>
              {s("app.heading")}
            </h2>
            <Link to={APP_PATH} className={TEXT_LINK}>
              {s("app.more")} <span aria-hidden="true">→</span>
            </Link>
          </div>
          <ul className="m-0 grid list-none grid-cols-1 gap-px overflow-hidden rounded-2xl border border-border bg-border p-0 shadow-sm sm:grid-cols-2" data-testid="home-features">
            {FEATURES.map((f) => (
              <li key={f} className="flex flex-col gap-2.5 bg-card p-7">
                <h3 className="m-0 font-display text-[21px] font-semibold">{t(`home.personal.features.${f}.title`)}</h3>
                <p className="m-0 text-[15px] leading-relaxed text-muted-foreground">{t(`home.personal.features.${f}.text`)}</p>
              </li>
            ))}
          </ul>
          <Link to={faceDataPolicyHref("family")} className={`${TEXT_LINK} self-start`} data-testid="privacy-app">
            {t("home.footer.appFaceData")} <span aria-hidden="true">→</span>
          </Link>
        </div>
      </section>

      {/* STUDIO'S DOORS: find your event photos, and Photohawk Studio for photographers */}
      <section aria-labelledby="home-more">
        <div className={`${WRAP} ${SECTION_Y} flex flex-col gap-8`}>
          <h2 id="home-more" className={H2}>
            {s("more.heading")}
          </h2>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(340px,100%),1fr))] gap-5" data-testid="home-studio-doors">
            <article className={STUDIO_CARD} aria-labelledby="home-find" data-testid="path-find" data-product="studio">
              <span className="flex items-center gap-2.5 text-[13px] font-semibold uppercase tracking-[0.1em] text-white/75">
                <span className="text-[#EA8A23]">
                  <SearchIcon size={18} />
                </span>
                {s("more.find.eyebrow")}
              </span>
              <h3 id="home-find" className="m-0 font-display text-[clamp(22px,2.2vw,28px)] font-bold leading-tight tracking-[-0.02em]">
                {s("more.find.title")}
              </h3>
              <p className="m-0 text-base leading-relaxed text-white/80">{s("more.find.text")}</p>
              <Link to={FIND_PATH} className={`${STUDIO_LINK} mt-auto`} data-testid="path-find-link">
                {s("more.find.link")} <span aria-hidden="true">→</span>
              </Link>
              <p className="m-0 text-sm text-white/60">{s("more.find.note")}</p>
            </article>
            <article className={STUDIO_CARD} aria-labelledby="home-studio" data-testid="path-events" data-product="studio">
              <span className="text-[13px] font-semibold uppercase tracking-[0.1em] text-white/75">{s("more.studio.eyebrow")}</span>
              <h3 id="home-studio" className="m-0">
                <ProductLockup product="studio" tone="dark" size="lg" />
              </h3>
              <p className="m-0 text-base leading-relaxed text-white/80">{s("more.studio.text")}</p>
              <Link to={STUDIO_PATH} className={`${STUDIO_LINK} mt-auto`} data-testid="path-studio-link">
                {s("more.studio.link")} <span aria-hidden="true">→</span>
              </Link>
            </article>
          </div>
        </div>
      </section>
    </SiteFrame>
  );
};

export default HomePage;
